/**
 * @file   src\mobile\pages\ResetPassword.js
 * @brief  This file is responsible for reset password
 * @date   April, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */
import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import EmailVerification from "../components/ResetPassword/EmailVerification";
import ChangePassword from "../components/ResetPassword/ChangePassword";
import {
  RESET_PWD_EMAIL_VERIFICATION,
  WANT_AD,
  USER_INFO_KEY,
} from "../../constants/common";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { resendEmail, verifyEmail } from "../../services/v2/signupService";
import {
  convertToNumber,
  emailValidation,
  passwordValidation,
} from "../../helpers/validations";
import { pageURLs } from "@/constants";
import { getLocalStorage } from "../../helpers/common";
import { resetPassword, updatePassword } from "../../services/v2/loginService";

const ResetPassword = () => {
  const [activePage, setActivePage] = useState();
  const [isEmailSent, setEmailSent] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isEmailView = activePage === RESET_PWD_EMAIL_VERIFICATION;
  const state = location.state;
  const isFromSettings = state && state?.isFromSettings;
  const initialFormData = {
    email: "",
    pwd: "",
    confPwd: "",
  };
  const initialErrors = {
    email: "",
    pwd: "",
    confPwd: "",
  };

  const [formData, setFormData] = useState(initialFormData);
  const [formErrors, setFormErrors] = useState(initialErrors);
  const [isShowPassWord, setPassWordShow] = useState(false);
  const [passwordType, setPassWordType] = useState("password");
  const [searchParams] = useSearchParams();

  /**
   * To handle submit
   */
  const handleSubmit = async () => {
    const isValid = await validate();

    if (isValid) {
      if (isEmailView) {
        const email = _.get(formData, "email", "");
        let reqPayload = { email: email };
        if (isFromSettings) {
          reqPayload.isFromSettings = true;
        }

        const res = await changePassword(reqPayload);
      } else {
        const pwd = _.get(formData, "pwd", "");
        const userId = searchParams.get("Id");
        const gCode = searchParams.get("g_code");
        const action = searchParams.get("action");
        if (userId) {
          const res = await changePassword({
            resetPassword: pwd,
            token: gCode,
            action: convertToNumber(action),
          });
          if (_.get(res, "errorCode", "") !== 0) {
            setFormErrors((formErrors) => ({
              ...formErrors,
              pwdError: _.get(res, "message", null),
            }));
          } else {
          }
        }
      }
    }
  };

  /**
   * To handle change password
   * @param {*} params
   * @returns
   */
  const changePassword = async (params) => {
    const email = _.get(params, "email", null);
    if (email) {
      return await resetPassword(params).then((response) => {
        if (response) {
          const resResult = _.get(response, "result", null);
          if (_.get(response, "errorCode", null) === 0) {
            // if (_.get(resResult, "IsEmailVerified", null) !== 0) {
            setEmailSent(true);
            toast.success("Reset link successfully sent to the email id.");
          }
          // } else {
          //   const message = _.get(response, "message", null);
          //   if (message) {
          //     setFormErrors((formErrors) => ({
          //       ...formErrors,
          //       email: message,
          //     }));
          //   }
          // }
        }
        return { isVerified: false, message: "something went wrong." };
      });
    } else {
      return await updatePassword(params)
        .then((response) => {
          if (response) {
            if (_.get(response, "errorCode", "") === 0) {
              toast.success("Password changed successfully. Please login.");
              navigate(pageURLs.mobileSignIn);
              if (isFromSettings) {
                toast.success("Password changed successfully.");
              }
            } else {
              return response;
            }
          }
          return null;
        })
        .catch((err) => {
          //TODO HANDLE THE ERROR CODE KEEPING THE MESSAGE STATIC AS OF NOW
          toast.error(`${err.response.data.message}`);
        });
    }
  };

  const onTextChange = (e) => {
    const textChangeField = e.target.name;
    setFormErrors((formErrors) => ({
      ...formErrors,
      [textChangeField]: "",
    }));
    const value = e.target.value;
    setFormData((formData) => ({ ...formData, [e.target.name]: value }));
  };

  const handlePassWordShow = () => {
    setPassWordShow(!isShowPassWord);
  };
  /**
   * To handle resend email
   */
  const resentEmail = () => {
    const email = _.get(formData, "email", "");
    const userInfo = {
      email: email,
      // Process: "ResetPwd",
    };

    resetPassword(userInfo).then((response) => {
      if (response) {
        if (_.get(response, "errorCode", false) === 0) {
          toast.success("Reset link sent successfully.");
        } else {
          toast.error("something went wrong.");
        }
      }
    });
  };

  useEffect(() => {
    const state = location.state;
    setActivePage(state && state?.activePage);
  }, []);

  useEffect(() => {
    if (!isEmailView) {
      if (isShowPassWord) {
        setPassWordType("text");
      } else {
        setPassWordType("password");
      }
    }
  }, [isShowPassWord]);

  useEffect(() => {
    setFormErrors(initialErrors);
    setFormData(initialFormData);
  }, [activePage]);

  const checkEmailExist = async (email) => {
    const userEmail = {
      Email: email,
    };
    return await verifyEmail(userEmail).then((response) => {
      return response?.result;
    });
  };

  const clearField = () => {
    setFormData((formData) => ({ ...formData, email: "" }));
  };
  /**
   * To handle validation
   * @returns
   */
  const validate = async () => {
    setFormErrors(initialErrors);
    let isValid = true;
    const email = _.get(formData, "email", null).trim();

    if (isEmailView) {
      if (!emailValidation(email)) {
        setFormErrors((formErrors) => ({
          ...formErrors,
          email: "InValid Email",
        }));
        isValid = false;
      } else {
        const res = await checkEmailExist(email);
        const userExist = _.get(res, "UserExists", false);
        const userVerified = _.get(res, "IsVerified", false);
        if (userExist && userVerified) {
          isValid = true;
        } else {
          setFormErrors((formErrors) => ({
            ...formErrors,
            email: "Invalid user",
          }));
          isValid = false;
        }
      }
      if (isFromSettings) {
        const userDetails = getLocalStorage(USER_INFO_KEY);
        const userInfo = _.get(userDetails, "userInfo", "");
        const userEmail = _.get(userInfo, "email", "");
        if (userEmail.trim() !== email.trim()) {
          setFormErrors((formErrors) => ({
            ...formErrors,
            email: "InValid Email",
          }));
          isValid = false;
        }
      }
    } else {
      const pwd = _.get(formData, "pwd", null).trim();
      const passWordError = passwordValidation(pwd);
      if (passWordError) {
        setFormErrors((formErrors) => ({
          ...formErrors,
          pwd: passWordError,
        }));
        isValid = false;
      } else {
        const confPwd = _.get(formData, "confPwd", null).trim();
        if (pwd !== confPwd) {
          setFormErrors((formErrors) => ({
            ...formErrors,
            confPwd: "Password not matching",
          }));
          isValid = false;
        } else {
          isValid = true;
        }
      }
    }

    return isValid;
  };

  const handleBackButtonClick = () => {
    if (isFromSettings) {
      navigate(pageURLs.mobileAccountSettings);
    } else {
      if (isEmailView) {
        navigate(pageURLs.mobileSignIn);
      } else {
        setActivePage(RESET_PWD_EMAIL_VERIFICATION);
      }
    }
  };

  if (isEmailView) {
    return (
      <EmailVerification
        handleSubmit={handleSubmit}
        email={_.get(formData, "email", null)}
        emailError={_.get(formErrors, "email", null)}
        onTextChange={onTextChange}
        clearField={clearField}
        handleBackButtonClick={handleBackButtonClick}
        isEmailSent={isEmailSent}
        resentEmail={resentEmail}
      />
    );
  } else {
    return (
      <ChangePassword
        handleSubmit={handleSubmit}
        handlePassWordShow={handlePassWordShow}
        formData={formData}
        formErrors={formErrors}
        isShowPassWord={isShowPassWord}
        passwordType={passwordType}
        onTextChange={onTextChange}
      />
    );
  }
};

export default ResetPassword;
